import { searchPublicMatchesPage, listUserMatchHistory, listUserBullseyeMatchHistory, listOwnedMatches, listOwnedBullseyeMatches } from "../../lib/leaderboardApi";
import { loadCompleteMatchHistory } from "../Leaderboard/matchHistoryPaging";
import { groupMyMatches, statusValue } from "../Leaderboard/myMatches";
import { loadOwnedPages, sortedDirectorMatches } from "../Matches/directorPortal";
import { matchPhase, normalizeMatch } from "../Matches/matchDiscovery";

// Discovery orders both disciplines together. Empty filtered pages can still have a cursor.
export async function loadHomePreview(status, signal, now = new Date()) {
  const matches = new Map(), seen = new Set();
  let nextToken;
  try {
    do {
      const page = await searchPublicMatchesPage({ status, sort: status === "completed" ? "latest" : "soonest", limit: 3, nextToken, signal });
      for (const match of page.data) {
        if (!match.matchId || !["nra_arc", "bullseye"].includes(match.competitionType)) continue;
        const phase = matchPhase(match, now);
        if (status === "completed" ? phase !== "completed" : !["scheduled", "in_progress"].includes(phase)) continue;
        matches.set(`${match.competitionType}:${match.matchId}`, normalizeMatch(match, match.competitionType));
      }
      nextToken = page.nextToken;
      if (matches.size >= 3) break;
      if (nextToken && (seen.has(nextToken) || seen.size >= 49)) throw new Error("Search did not finish.");
      if (nextToken) seen.add(nextToken);
    } while (nextToken);
    return { data: [...matches.values()].slice(0, 3) };
  } catch (error) {
    if (!matches.size) throw error;
    return { data: [...matches.values()].slice(0, 3), incomplete: true };
  }
}

export async function loadHomeEntries(userId) {
  const results = await Promise.allSettled([
    loadCompleteMatchHistory(listUserMatchHistory, userId),
    loadCompleteMatchHistory(listUserBullseyeMatchHistory, userId),
  ]);
  if (results.every(result => result.status === "rejected")) throw new Error("Your entries could not load.");
  return {
    data: results.flatMap((result, index) => result.status === "fulfilled"
      ? result.value.data.map(entry => ({ ...entry, family: index ? "bullseye" : "arc" })) : []),
    incomplete: results.some(result => result.status === "rejected" || result.value.error),
  };
}

export function nextHomeEntry(entries, now = new Date()) {
  // A waitlist request is never a reserved competitor entry.
  return groupMyMatches(entries.filter(entry => ["pending", "confirmed", "checked_in"].includes(statusValue(entry))), now).upcoming[0];
}

export async function loadHomeManaged(userId) {
  const results = await Promise.allSettled([
    loadOwnedPages(listOwnedMatches, userId),
    loadOwnedPages(listOwnedBullseyeMatches, userId),
  ]);
  if (results.every(result => result.status === "rejected")) throw new Error("Your managed matches could not load.");
  const matches = results.flatMap((result, index) => result.status === "fulfilled"
    ? result.value.map(match => ({ ...match, family: index ? "bullseye" : "arc" })) : []);
  return {
    data: ["live", "upcoming", "past"].flatMap(view => sortedDirectorMatches(matches, view)).slice(0, 2),
    incomplete: results.some(result => result.status === "rejected"),
  };
}
